import { useState, useEffect } from 'react';
import { Star, ExternalLink, Loader2, RefreshCw, MessageSquare } from 'lucide-react';
import GoogleReviewCard from './GoogleReviewCard';
import Button from './Button';

const GOOGLE_MAPS_URL = 'https://www.google.com/maps/place/Mian+Tufail+Diagnostic+Centre';
const REVIEWS_URL = 'https://reviews.mtufaildiagnosticcenter.com';

/**
 * Google Reviews Section - fetches and displays 5-star Google reviews
 */
const GoogleReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [allReviews, setAllReviews] = useState([]);
  const [meta, setMeta] = useState({ overallRating: 0, totalReviews: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchReviews = async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await fetch('/api/google-reviews');
      const data = await response.json();
      if (data.success && data.data?.reviews?.length > 0) {
        setAllReviews(data.data.reviews);
        // Display only 5-star reviews (max 5)
        const fiveStarReviews = data.data.reviews.filter((r) => r.rating === 5).slice(0, 4);
        setReviews(fiveStarReviews);
        setMeta({
          overallRating: data.data.overallRating,
          totalReviews: data.data.totalReviews,
        });
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  // Calculate rating distribution from available reviews
  const ratingCounts = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: allReviews.filter((r) => r.rating === star).length,
  }));
  const maxCount = Math.max(...ratingCounts.map((r) => r.count), 1);

  return (
    <section className="py-20 bg-neutral-50">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block text-primary-600 font-semibold text-sm uppercase tracking-wider mb-3">
            Google Reviews
          </span>
          <h2 className="section-title mb-4">
            What Our <span className="gradient-text">Patients Say</span> on Google
          </h2>
          <p className="section-subtitle mx-auto mb-8">
            Real reviews from verified Google users who visited our diagnostic center.
          </p>

          {/* Stats Card */}
          {!loading && !error && meta.totalReviews > 0 && (
            <div className="inline-flex flex-col sm:flex-row items-center gap-6 bg-white rounded-2xl px-8 py-6 shadow-soft border border-neutral-100">
              {/* Overall Rating */}
              <div className="text-center">
                <div className="text-5xl font-bold text-neutral-900 leading-none mb-1">
                  {meta.overallRating.toFixed(1)}
                </div>
                <div className="flex gap-0.5 justify-center mb-1">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${
                        i < Math.round(meta.overallRating)
                          ? 'fill-yellow-400 text-yellow-400'
                          : i < meta.overallRating
                          ? 'fill-yellow-200 text-yellow-300'
                          : 'text-neutral-200'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-sm text-neutral-500">{meta.totalReviews} reviews</p>
              </div>

              {/* Divider */}
              <div className="hidden sm:block w-px h-20 bg-neutral-200" />
              <div className="sm:hidden w-32 h-px bg-neutral-200" />

              {/* Rating Bars */}
              <div className="space-y-1.5 min-w-[180px]">
                {ratingCounts.map(({ star, count }) => (
                  <div key={star} className="flex items-center gap-2 text-sm">
                    <span className="text-neutral-500 w-3 text-right">{star}</span>
                    <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                    <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-yellow-400 rounded-full transition-all duration-500"
                        style={{ width: `${(count / maxCount) * 100}%` }}
                      />
                    </div>
                    <span className="text-neutral-400 text-xs w-5 text-right">{count}</span>
                  </div>
                ))}
              </div>

              {/* Divider */}
              <div className="hidden sm:block w-px h-20 bg-neutral-200" />
              <div className="sm:hidden w-32 h-px bg-neutral-200" />

              {/* Google Badge + Write Review Button */}
              <div className="flex flex-col items-center gap-3">
                <svg className="w-8 h-8" viewBox="0 0 24 24" aria-label="Google">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                <a
                  href={REVIEWS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                >
                  <MessageSquare className="w-4 h-4" />
                  Write a Review
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="text-center py-12">
            <p className="text-neutral-500 mb-4">Unable to load reviews right now.</p>
            <Button variant="outline" size="sm" icon={RefreshCw} onClick={fetchReviews}>
              Try Again
            </Button>
          </div>
        )}

        {/* Reviews Grid */}
        {!loading && !error && reviews.length > 0 && (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-10">
              {reviews.map((review, index) => (
                <GoogleReviewCard key={index} review={review} />
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href={GOOGLE_MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="outline" icon={ExternalLink} iconPosition="right">
                  See All Reviews on Google
                </Button>
              </a>
              <a
                href={REVIEWS_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="primary" icon={MessageSquare} iconPosition="right">
                  Leave a Review
                </Button>
              </a>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default GoogleReviewsSection;
